import { post_exchangeUserUploadFile } from "@/api/exchange-user";
import { isKycStaticStorageUrl } from "@/utils/kycImageProxy";

const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpg", "image/jpeg"];
const HYPERPAY_KYC_SELECT_MAX_BYTES = 10 * 1024 * 1024;
/** 上传目标体积；大图压缩后需留余量，避免 dev 代理/网关对 ~1MB 边界请求断连 */
const HYPERPAY_KYC_UPLOAD_MAX_BYTES = 768 * 1024;

const IMAGE_ERROR_KEY_MAP = {
  doc: {
    format: "spikexCard.formImageFormatTip",
    size: "spikexCard.formDocImageSizeTip",
    sign_size: "spikexCard.formDocImageSizeTip",
    compress_size: "spikexCard.formDocImageCompressTooLarge",
    compress: "spikexCard.formDocImageCompressFailed",
    fetch: "spikexCard.formDocImageFetchFailed",
    upload: "spikexCard.formDocImageUploadFailed",
    network: "spikexCard.formDocImageNetworkFailed",
  },
  mix: {
    format: "spikexCard.formImageFormatTip",
    size: "spikexCard.formMixImageSizeTip",
    sign_size: "spikexCard.formMixImageSizeTip",
    compress_size: "spikexCard.formMixImageCompressTooLarge",
    compress: "spikexCard.formMixImageCompressFailed",
    fetch: "spikexCard.formMixImageFetchFailed",
    upload: "spikexCard.formMixImageUploadFailed",
    network: "spikexCard.formMixImageNetworkFailed",
  },
  sign: {
    format: "spikexCard.formImageFormatTip",
    size: "spikexCard.formImageSizeTipSign",
    sign_size: "spikexCard.formImageSizeTipSign",
    compress_size: "spikexCard.formSignImageCompressTooLarge",
    compress: "spikexCard.formSignImageCompressFailed",
    fetch: "spikexCard.formSignImageFetchFailed",
    upload: "spikexCard.formSignImageUploadFailed",
    network: "spikexCard.formSignImageNetworkFailed",
  },
};

function getImageErrorFieldGroup(field) {
  const normalized = String(field || "").trim();
  if (normalized === "mix_doc") return "mix";
  if (normalized === "sign_img") return "sign";
  return "doc";
}

function createHyperpayKycImageError(code, field) {
  const error = new Error(code);
  if (field) error.field = field;
  return error;
}

function throwHyperpayKycImageError(code, field) {
  throw createHyperpayKycImageError(code, field);
}

function extractPathFromUploadedUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;

  try {
    const normalized = raw.startsWith("//") ? `https:${raw}` : raw;
    if (/^https?:\/\//i.test(normalized)) {
      return new URL(normalized).pathname || "";
    }
  } catch (_) {
    /* ignore */
  }

  return "";
}

/** 从 KYC 预填静态图 URL 解析 HyperPay 所需的相对路径 */
export function extractKycImageRelativePath(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;

  try {
    const normalized = raw.startsWith("//") ? `https:${raw}` : raw;
    const parsed = new URL(normalized);
    if (isKycStaticStorageUrl(parsed.href)) {
      return parsed.pathname || "";
    }
  } catch (_) {
    /* ignore */
  }

  return "";
}

/** 从 exchange-user uploadFile 响应解析相对路径 */
export function extractUploadRelativePath(payload) {
  if (payload == null) return "";

  if (Array.isArray(payload)) {
    return extractPathFromUploadedUrl(payload[0]);
  }

  if (typeof payload === "string") {
    return extractPathFromUploadedUrl(payload);
  }

  const candidate =
    payload.data ??
    payload.result ??
    payload.url ??
    payload.path ??
    payload.fileUrl ??
    payload.filePath ??
    payload.relativePath ??
    payload.absolutePath ??
    "";

  if (Array.isArray(candidate)) {
    return extractPathFromUploadedUrl(candidate[0]);
  }

  if (candidate && typeof candidate === "object") {
    return extractPathFromUploadedUrl(
      candidate.absolutePath ||
        candidate.url ||
        candidate.path ||
        candidate.fileUrl ||
        candidate.filePath ||
        candidate.relativePath ||
        ""
    );
  }

  return extractPathFromUploadedUrl(candidate);
}

export function getHyperpayKycImageLimit(field) {
  return HYPERPAY_KYC_SELECT_MAX_BYTES;
}

export function getHyperpayKycUploadMaxBytes() {
  return HYPERPAY_KYC_UPLOAD_MAX_BYTES;
}

/** 低端机跳过 Web Worker，避免内存/线程不足导致压缩失败 */
export function shouldUseWebWorkerForKycCompression() {
  if (typeof Worker === "undefined" || typeof navigator === "undefined") return false;

  const cores = navigator.hardwareConcurrency || 0;
  const memoryGb = navigator.deviceMemory || 0;

  if (cores > 0 && cores <= 2) return false;
  if (memoryGb > 0 && memoryGb <= 2) return false;

  return true;
}

export function resolveHyperpayKycImageErrorKey(error, field) {
  const code = String(error?.message || "").trim();
  const group = getImageErrorFieldGroup(error?.field || field);
  return IMAGE_ERROR_KEY_MAP[group][code] || IMAGE_ERROR_KEY_MAP.doc.upload;
}

export function isHyperpayKycImageError(error) {
  return [
    "format",
    "size",
    "sign_size",
    "compress",
    "compress_size",
    "fetch",
    "upload",
    "network",
  ].includes(String(error?.message || "").trim());
}

function replaceFileExtension(name, ext) {
  const base = String(name || "kyc-image").replace(/\.[^.]+$/, "");
  return `${base}${ext}`;
}

function toUploadFile(blob, fallbackName) {
  if (blob instanceof File) return blob;
  return new File([blob], fallbackName || "kyc-image.jpg", {
    type: resolveHyperpayKycImageMimeType(blob),
    lastModified: Date.now(),
  });
}

function finalizeCompressedFile(sourceFile, compressed, maxBytes, field) {
  if (!compressed?.size || compressed.size > maxBytes) {
    throwHyperpayKycImageError("compress_size", field);
  }

  if (compressed instanceof File) return compressed;
  return new File([compressed], replaceFileExtension(sourceFile.name, ".jpg"), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

async function runImageCompression(sourceFile, profile) {
  const { default: imageCompression } = await import("browser-image-compression");
  return imageCompression(sourceFile, {
    maxSizeMB: profile.maxSizeMB,
    maxWidthOrHeight: profile.maxWidthOrHeight,
    useWebWorker: profile.useWebWorker,
    fileType: "image/jpeg",
    initialQuality: profile.initialQuality,
    alwaysKeepResolution: false,
  });
}

/** 源图越大，缩放与质量越激进，并避免 Web Worker OOM */
function resolveCompressionProfile(sourceFile, maxBytes) {
  const sizeMb = sourceFile.size / (1024 * 1024);
  const baseMaxSizeMB = maxBytes / (1024 * 1024);

  if (sizeMb >= 5) {
    return {
      maxSizeMB: Math.min(baseMaxSizeMB, 0.65),
      maxWidthOrHeight: 1600,
      initialQuality: 0.78,
      useWebWorker: false,
    };
  }
  if (sizeMb >= 3) {
    return {
      maxSizeMB: Math.min(baseMaxSizeMB, 0.72),
      maxWidthOrHeight: 2048,
      initialQuality: 0.82,
      useWebWorker: false,
    };
  }
  return {
    maxSizeMB: baseMaxSizeMB,
    maxWidthOrHeight: 3072,
    initialQuality: 0.88,
    useWebWorker: shouldUseWebWorkerForKycCompression(),
  };
}

/** 超过 upload 上限时压缩为 JPEG，目标体积 ≤ maxBytes */
export async function compressHyperpayKycImageFile(
  file,
  maxBytes = HYPERPAY_KYC_UPLOAD_MAX_BYTES,
  field
) {
  const blob = file instanceof Blob ? file : file?.originFileObj;
  if (!blob?.size) throwHyperpayKycImageError("format", field);
  if (blob.size <= maxBytes) return file instanceof File ? file : blob;

  const sourceFile = toUploadFile(blob, file?.name || blob.name);
  const profile = resolveCompressionProfile(sourceFile, maxBytes);

  try {
    if (profile.useWebWorker) {
      try {
        const compressed = await runImageCompression(sourceFile, profile);
        return finalizeCompressedFile(sourceFile, compressed, maxBytes, field);
      } catch (workerError) {
        if (workerError?.message === "compress_size") throw workerError;
        const fallbackProfile = { ...profile, useWebWorker: false };
        const compressed = await runImageCompression(sourceFile, fallbackProfile);
        return finalizeCompressedFile(sourceFile, compressed, maxBytes, field);
      }
    }

    const compressed = await runImageCompression(sourceFile, profile);
    return finalizeCompressedFile(sourceFile, compressed, maxBytes, field);
  } catch (error) {
    if (error?.message === "compress_size") throw error;
    throwHyperpayKycImageError("compress", field);
  }
}

export function resolveHyperpayKycImageMimeType(file) {
  if (file?.type && SUPPORTED_IMAGE_TYPES.includes(file.type)) return file.type;
  return "image/jpeg";
}

export function validateHyperpayKycImageFile(file, field) {
  const mimeType = resolveHyperpayKycImageMimeType(file);
  if (!SUPPORTED_IMAGE_TYPES.includes(mimeType)) {
    throwHyperpayKycImageError("format", field);
  }
  if (!file?.size || file.size > getHyperpayKycImageLimit(field)) {
    throwHyperpayKycImageError(field === "sign_img" ? "sign_size" : "size", field);
  }
}

export async function uploadHyperpayKycImage(file, filename, { onPhase, field } = {}) {
  const blob = file instanceof Blob ? file : file?.originFileObj;
  if (!blob) throwHyperpayKycImageError("upload", field);

  const sourceFile =
    file instanceof File
      ? file
      : toUploadFile(blob, filename || file?.name || "kyc-image.jpg");

  if (sourceFile.size > HYPERPAY_KYC_UPLOAD_MAX_BYTES) {
    onPhase?.("compress");
  }
  const compressed = await compressHyperpayKycImageFile(sourceFile, HYPERPAY_KYC_UPLOAD_MAX_BYTES, field);
  onPhase?.("upload");
  const uploadBlob = compressed instanceof Blob ? compressed : blob;
  const uploadName =
    compressed instanceof File ? compressed.name : filename || file?.name || sourceFile.name || "kyc-image.jpg";

  const formData = new FormData();
  formData.append("file", uploadBlob, uploadName);

  let response;
  try {
    response = await post_exchangeUserUploadFile(formData);
  } catch (error) {
    const code = String(error?.message || "").trim();
    throwHyperpayKycImageError(code === "network" ? "network" : "upload", field);
  }

  const path = extractUploadRelativePath(response);
  if (!path) throwHyperpayKycImageError("upload", field);
  return path;
}

/** 将 Upload fileList 最后一项解析为 HyperPay KYC 图片相对路径 */
export async function resolveHyperpayKycImagePathFromFileList(fileList, field, { loadRemoteBlob } = {}) {
  const item = Array.isArray(fileList) && fileList.length ? fileList[fileList.length - 1] : null;
  if (!item) return "";

  if (item.uploadPath) return item.uploadPath;

  const remoteUrl = item.url || item.thumbUrl;
  if (remoteUrl) {
    const existingPath = extractKycImageRelativePath(remoteUrl);
    if (existingPath) return existingPath;
    if (typeof loadRemoteBlob === "function") {
      const blob = await loadRemoteBlob(remoteUrl);
      validateHyperpayKycImageFile(blob, field);
      return uploadHyperpayKycImage(blob, item.name || `${field}.jpg`, { field });
    }
  }

  if (item.originFileObj) {
    validateHyperpayKycImageFile(item.originFileObj, field);
    return uploadHyperpayKycImage(item.originFileObj, undefined, { field });
  }

  throwHyperpayKycImageError("fetch", field);
}

/** 预填 KYC 图片时，若 URL 已在静态存储上则提前解析相对路径（用户本地上传的不处理） */
export async function prefetchHyperpayKycUploadPath(fileList) {
  const item = Array.isArray(fileList) && fileList.length ? fileList[fileList.length - 1] : null;
  if (!item || item.uploadPath || item.originFileObj || item.isKycPrefill) return fileList;

  const remoteUrl = item.url || item.thumbUrl;
  if (!remoteUrl) return fileList;

  const uploadPath = extractKycImageRelativePath(remoteUrl);
  if (!uploadPath) return fileList;

  return [{ ...item, uploadPath }];
}
